import { DownloadService } from "@/services/admin-services/DownloadService";
import { DynamicDownload } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language } from "@/services/global-services/models";
import { useDataStore } from "@/stores";
import { getBytes } from "@/utils/getBytes";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Divider,
    Grid,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { AxiosError, AxiosProgressEvent, CanceledError } from "axios";
import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { FileDrop } from "react-file-drop";
import DownloadDisciplineDropdown from "./components/DownloadDisciplineDropdown";
import DownloadLanguagesDropdown from "./components/DownloadLanguagesDropdown";
import DownloadSoftwareDropdown from "./components/DownloadSoftwareDropdown";
import DownloadVersionDropdown from "./components/DownloadVersionDropdown/DownloadVersionDropdown";

interface CreateDialogProps {
    open: boolean;
    languages: Array<Language>;
    onClose: DialogProps["onClose"];
    onSubmit: (file: DynamicDownload) => void;
}

export const CreateCombinedDialog = ({ open, languages, onClose, onSubmit }: CreateDialogProps) => {
    const [versionDataLoading, setVersionDataLoading] = useState<boolean>(false);
    const versions = useDataStore((state) => state.versions);
    const disciplines = useDataStore((state) => state.disciplines);
    const software = useDataStore((state) => state.software);

    const setDisciplines = useDataStore((state) => state.setDisciplines);
    const setSoftware = useDataStore((state) => state.setSoftware);

    const [file, setFile] = useState<File | null>(null);
    const [filename, setFilename] = useState<string>("");
    const [selectedVersion, setSelectedVersion] = useState<string>(versions.find((x) => x.isDefault)?.name || "");
    const [language, setLanguage] = useState<string>("");
    const [selectedDiscipline, setSelectedDiscipline] = useState<string>("");
    const [selectedSoftware, setSelectedSoftware] = useState<Array<string>>([]);
    const [error, setError] = useState<string | null>(null);

    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const downloadService = new DownloadService("int");
    const localeService = useMemo(() => new LocaleService("int", "en"), []);

    useEffect(() => {
        if (!file) return;
        setFilename(file.name);
    }, [file]);

    useEffect(() => {
        if (!selectedVersion) return;
        if (disciplines[selectedVersion] && software[selectedVersion]) return;

        setVersionDataLoading(true);

        const promises: Promise<void>[] = [];
        promises.push(
            Promise.all([
                localeService.getDisciplines(selectedVersion),
                localeService.getSoftware(selectedVersion),
            ]).then(([disciplines, software]) => {
                setSelectedDiscipline("");
                setDisciplines(selectedVersion, disciplines);
                setSelectedSoftware([]);
                setSoftware(selectedVersion, software);
            })
        );

        Promise.all(promises).then(() => setVersionDataLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVersion]);

    const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (!files) return;
        setFile(files[0]);
        setError(null);
    };

    const onFileSelect = () => {
        if (!inputRef.current) return;
        inputRef.current.click();
    };

    const onFileDrop = (files: FileList | null) => {
        if (!files) return;
        setFile(files[0]);
    };

    const onFormSubmit = (event: FormEvent) => {
        event.preventDefault();

        if (!filename || !language || !file || !selectedDiscipline) return;

        setIsSaving(true);

        const form = new FormData();
        form.append("File", file);
        form.append("uploadLanguage", language);
        form.append("TargetDiscipline", selectedDiscipline);
        form.append("TargetSoftware", selectedSoftware[0]);

        const abort = new AbortController();
        abortRef.current = abort;

        downloadService
            .createDynamic(selectedVersion, form, abort, onUploadProgress)
            .then((file) => {
                onSubmit(file);
            })
            .catch((err) => {
                if (err instanceof CanceledError) return;
                if (err instanceof AxiosError) {
                    const error = err as AxiosError;
                    if (error.response?.data) setError(error.response.data as string);
                }
            })
            .finally(() => {
                abortRef.current = null;
                setIsSaving(false);
            });
    };

    const onUploadProgress = (event: AxiosProgressEvent) => {
        setUploadProgress((event.progress || 0) * 100);
    };

    const onCancel = () => {
        abortRef.current?.abort();
        if (onClose) onClose({}, "escapeKeyDown");
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"} fullScreen={fullScreen}>
            <form onSubmit={(event) => onFormSubmit(event)}>
                <DialogTitle>Process new combined download</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        pointerEvents: isSaving ? "none" : "auto",
                    }}
                >
                    <FileDrop onTargetClick={onFileSelect} onDrop={onFileDrop}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                borderColor: "text.secondary",
                                borderWidth: 2,
                                borderStyle: "dashed",
                                borderRadius: 5,
                                minHeight: 150,
                                placeContent: "center",
                                alignItems: "center",
                                opacity: isSaving ? 0.5 : 1,
                                padding: 3,
                            }}
                        >
                            {file ? (
                                <>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ userSelect: "none" }}>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ userSelect: "none" }}>
                                        ({getBytes(file.size)})
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="subtitle2" color="text.secondary" sx={{ userSelect: "none" }}>
                                    DROP FILE HERE OR BROWSE
                                </Typography>
                            )}
                        </Box>
                    </FileDrop>
                    <input
                        value=""
                        style={{ visibility: "collapse", opacity: 0 }}
                        ref={inputRef}
                        type="file"
                        onChange={onFileInputChange}
                    />

                    <Divider sx={{ marginY: 3 }} />
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <DownloadVersionDropdown
                                value={selectedVersion}
                                onChange={(e) => setSelectedVersion(e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadLanguagesDropdown
                                items={languages}
                                value={language}
                                onChange={(e) => setLanguage(e.target.value as string)}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadDisciplineDropdown
                                selectedVersions={[selectedVersion]}
                                value={selectedDiscipline}
                                onChange={(e) => setSelectedDiscipline(e.target.value)}
                                loading={versionDataLoading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadSoftwareDropdown
                                selectedVersions={[selectedVersion]}
                                value={selectedSoftware}
                                onChange={(e) => setSelectedSoftware(e.target.value as string[])}
                                loading={versionDataLoading}
                            />
                        </Grid>
                    </Grid>

                    {(isSaving || Boolean(error)) && (
                        <LinearProgress
                            variant="determinate"
                            color={error ? "error" : "primary"}
                            value={uploadProgress}
                            sx={{ height: 15, mt: 2 }}
                        />
                    )}
                    {Boolean(error) && <p className="mt-2 font-medium italic text-red-500">{error}</p>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button variant="contained" type="submit" disabled={isSaving}>
                        Process
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
